import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBullseye, FaEye, FaSync, FaChevronDown, FaEdit, FaPlus, FaDollarSign, FaClock, FaInfo } from 'react-icons/fa';
import Card from '../common/Card';
import PDFModal from '../common/PDFModal';
import DetailsModal from '../common/DetailsModal';
import ValidationModal from './BloodTestWarningModal';
import axios from 'axios';
import { PatientContext } from '../../contexts/PatientContext';

const PatientCard = ({ patient }) => {
  let bloodReport = null;
  let dexaReport = { report_url: "https://www.orimi.com/pdf-test.pdf" };
  let vo2maxReport = null;
  let summaryReport = null;
  let strengthReport = null;
  let alzheimerReport = null;
  let fatLossReport = null;
  const [isNotifying, setIsNotifying] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const { loadPatients } = useContext(PatientContext);

  try {
    if (patient.report_urls && Array.isArray(patient.report_urls)) {
      bloodReport = patient.report_urls.find(report => report.report_type === 'bloodReport');
      dexaReport = patient.report_urls.find(report => report.report_type === 'dexaReport');
      vo2maxReport = patient.report_urls.find(report => report.report_type === 'vo2maxReport');
      summaryReport = patient.report_urls.find(report => report.report_type === 'summaryReport');
      strengthReport = patient.report_urls.find(report => report.report_type === 'strengthReport');
      alzheimerReport = patient.report_urls.find(report => report.report_type === 'alzheimerReport');
      fatLossReport = patient.report_urls.find(report => report.report_type === 'fatLossReport');
    }
  } catch (error) {
    console.error('Error processing report URLs:', error);
    if (error instanceof TypeError) {
      console.error('TypeError: report_urls is not an array');
    }
  }

  const navigate = useNavigate();
  const [isBloodPDFModalOpen, setBloodPDFModalOpen] = useState(false);
  const [isDexaPDFModalOpen, setDexaPDFModalOpen] = useState(false);
  const [isVO2MaxPDFModalOpen, setVO2MaxPDFModalOpen] = useState(false);
  const [isSummaryPDFModalOpen, setSummaryPDFModalOpen] = useState(false);
  const [isStrengthPDFModalOpen, setStrengthPDFModalOpen] = useState(false);
  const [isAlzheimerPDFModalOpen, setAlzheimerPDFModalOpen] = useState(false);
  const [isFatLossPDFModalOpen, setFatLossPDFModalOpen] = useState(false);
  const [isDetailsModalOpen, setDetailsModalOpen] = useState(false);
  
  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Blood report confirmation state
  const [showBloodConfirm, setShowBloodConfirm] = useState(false);
  
  // Blood test warning modal state
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [pendingBloodTestRequest, setPendingBloodTestRequest] = useState(null);
  
  const HEALTH_CATEGORIES = [
    { id: 1, name: 'Metabolic Health' },
    { id: 2, name: 'Heart Health' },
    { id: 3, name: 'Cognition and Memory' },
    { id: 4, name: 'Endurance and Fitness' },
    { id: 5, name: 'Immune Health' },
    { id: 6, name: 'Gut Health' },
    { id: 7, name: 'Inflammation' },
    { id: 8, name: 'Bone Health' },
    { id: 9, name: 'Female Hormones' },
    { id: 10, name: 'Male Hormones' },
    { id: 11, name: 'Sleep and Recovery' }
  ];

  // Helper function to normalize strings for comparison (remove spaces, lowercase)
  const normalizeString = (str) => {
    return str.toLowerCase().replace(/\s+/g, '');
  };

  // Create normalized category map for matching
  const getCategoryByNormalizedName = (testName) => {
    if (!testName) return null;
    const normalizedTestName = normalizeString(testName);
    
    for (const category of HEALTH_CATEGORIES) {
      if (normalizeString(category.name) === normalizedTestName) {
        return category.id;
      }
    }
    return null;
  };

  // Mapping of test codes to health category IDs
  const TEST_CODE_TO_CATEGORY = {
    'P1942': 1,   // Metabolic Health
    'P11942': 1,  // Metabolic Health (alternate format)
    'P1943': 2,   // Heart Health
    'P11943': 2,  // Heart Health (alternate format)
    'P1944': 3,   // Cognition and Memory
    'P11944': 3,  // Cognition and Memory (alternate format)
    'P1945': 4,   // Endurance and Fitness
    'P11945': 4,  // Endurance and Fitness (alternate format)
    'P1946': 5,   // Immune Health
    'P11946': 5,  // Immune Health (alternate format)
    'P1948': 7,   // Inflammation
    'P11948': 7,  // Inflammation (alternate format)
    'P1949': 8,   // Bone Health
    'P11949': 8,  // Bone Health (alternate format)
    'P1950': 9,   // Female Hormones
    'P11950': 9,  // Female Hormones (alternate format)
    'P1951': 10,  // Male Hormones
    'P11951': 10, // Male Hormones (alternate format)
    'P1952': 11,  // Sleep and Recovery
    'P11952': 11  // Sleep and Recovery (alternate format)
  };

  // Category selection state - preselect based on appointment tests
  const [selectedCategories, setSelectedCategories] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
  
  // Blood report additional options
  const [includeAbnormalBiomarkers, setIncludeAbnormalBiomarkers] = useState(true);
  const [includeResultsAtGlance, setIncludeResultsAtGlance] = useState(true);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize selected categories based on appointment tests
  useEffect(() => {
    if (patient.appointmentTests && Array.isArray(patient.appointmentTests) && patient.appointmentTests.length > 0) {
      const categoryIds = new Set();
      patient.appointmentTests.forEach(test => {
        let categoryId = null;
        
        // Try matching by test code first
        const testCode = test.availableTest?.test_code;
        if (testCode && TEST_CODE_TO_CATEGORY[testCode]) {
          categoryId = TEST_CODE_TO_CATEGORY[testCode];
        }
        
        // If test code didn't match, try matching by test name
        if (!categoryId) {
          const testName = test.availableTest?.name;
          if (testName) {
            // Extract category name from test name (e.g., "Blood Mini - Heart Health" -> "Heart Health")
            const parts = testName.split('-');
            if (parts.length > 1) {
              const categoryName = parts[parts.length - 1].trim();
              categoryId = getCategoryByNormalizedName(categoryName);
            }
          }
        }
        
        if (categoryId) {
          categoryIds.add(categoryId);
        }
      });
      if (categoryIds.size > 0) {
        setSelectedCategories(Array.from(categoryIds));
      }
    }
  }, [patient.appointmentTests]);

  const handleNotifyBloodTest = async () => {
    if (!patient.sukra_order_id) return;
    
    setIsNotifying(true);
    
    try {
      const response = await axios.post(
        'https://vitaldev.vitalinsights.in/content/blood-test-report-notification',
        {
          data: {
            id: patient.id,
            sid_no: patient.sukra_order_id,
            category: selectedCategories.map(id => id.toString()),
            includeAbnormalBiomarkers: includeAbnormalBiomarkers,
            includeResultsAtGlance: includeResultsAtGlance
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer d3LnczLm9yZy8yMDAxLzA0L3htbGRzaWctbW9yZSNobWFjLXNoYTI1NiIsInR5cCI6IkpXVCJ9'
          },
          timeout: 50000,
          validateStatus: () => true // Don't throw on any status code
        }
      );
      
      // Check if response has nested error status
      const responseData = response.data?.data || response.data;
      if (responseData?.status === false) {
        setIsNotifying(false);
        setShowBloodConfirm(false);
        
        const message = responseData?.message || 'Error generating blood report';
        
        // Check if this is a warning message (contains "Warning:")
        if (message.includes('Warning:')) {
          // Show warning modal instead of error notification
          setWarningMessage(message);
          setPendingBloodTestRequest({
            id: patient.id,
            sid_no: patient.sukra_order_id,
            category: selectedCategories.map(id => id.toString())
          });
          setShowWarningModal(true);
          return;
        }
        
        // Regular error message
        setNotificationMessage(message);
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
        return;
      }
      
      setNotificationMessage('Blood report request submitted successfully. Please check back in about 10 minutes.');
      setShowNotification(true);
      setShowBloodConfirm(false);
      // setSelectedCategories([]);
      
      // Refresh the patients data to update the UI
      await loadPatients();
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    } catch (error) {
      console.error('Error sending blood test notification:', error);
      setShowBloodConfirm(false);
      
      if (error.code === 'ECONNABORTED') {
        setNotificationMessage('Request timeout. Please try again.');
      } else {
        setNotificationMessage('Failed to send blood test notification. Please try again.');
      }
      setShowNotification(true);
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    } finally {
      setIsNotifying(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleBloodConfirmClose = () => {
    setShowBloodConfirm(false);
    // setSelectedCategories([]);
  };

  const handleForceBloodTestCreate = async (password) => {
    if (!pendingBloodTestRequest) return;

    setIsNotifying(true);

    try {
      // Hash the password (simple hash for now - can be improved)
      const hashedPassword = btoa(password); // Base64 encoding as placeholder

      const response = await axios.post(
        'https://vitaldev.vitalinsights.in/content/blood-test-report-notification',
        {
          data: {
            ...pendingBloodTestRequest,
            force: true,
            password: hashedPassword
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer d3LnczLm9yZy8yMDAxLzA0L3htbGRzaWctbW9yZSNobWFjLXNoYTI1NiIsInR5cCI6IkpXVCJ9'
          },
          timeout: 50000
        }
      );

      // Check if response is successful
      const responseData = response.data?.data || response.data;
      if (responseData?.status === false) {
        const message = responseData?.message || 'Error generating blood report';
        setNotificationMessage(message);
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
        setIsNotifying(false);
        return;
      }

      setNotificationMessage('Blood report request submitted successfully. Please check back in about 10 minutes.');
      setShowNotification(true);
      setShowWarningModal(false);
      setSelectedCategories([]);
      setPendingBloodTestRequest(null);

      // Refresh the patients data to update the UI
      await loadPatients();

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    } catch (error) {
      console.error('Error sending blood test notification with force:', error);

      if (error.code === 'ECONNABORTED') {
        setNotificationMessage('Request timeout. Please try again.');
      } else {
        setNotificationMessage('Failed to send blood test notification. Please try again.');
      }
      setShowNotification(true);

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    } finally {
      setIsNotifying(false);
    }
  };

  // Check if patient has VO2 MAX test code (003553)
  const hasVO2MaxTest = () => {
    if (!patient.appointmentTests || !Array.isArray(patient.appointmentTests)) {
      return false;
    }
    return patient.appointmentTests.some(test => 
      test.availableTest?.test_code === '003553'
    );
  };

  // Check if patient has DEXA test code (003554)
  const hasDexaTest = () => {
    if (!patient.appointmentTests || !Array.isArray(patient.appointmentTests)) {
      return false;
    }
    return patient.appointmentTests.some(test => 
      test.availableTest?.test_code === '003554'
    );
  };

  // Check if patient has Summary Report test code (003555)
  const hasSummaryReportTest = () => {
    const hasBlood = hasBloodTest();
    const hasActiveGoal = getActiveProfileGoal();
    return hasBlood && hasActiveGoal;
  };

  // Check if patient has Strength Report test code (003555 or 003556)
  const hasStrengthReportTest = () => {
    if (!patient.appointmentTests || !Array.isArray(patient.appointmentTests)) {
      return false;
    }
    return patient.appointmentTests.some(test => 
      test.availableTest?.test_code === '003555' || test.availableTest?.test_code === '003556'
    );
  };

  // Check if patient has Alzheimer Report (has blood test)
  const hasAlzheimerReportTest = () => {
    return hasBloodTest();
  };

  // Check if patient has Fat Loss Report (has blood test)
  const hasFatLossReportTest = () => {
    if (!patient.appointmentTests || !Array.isArray(patient.appointmentTests)) {
      return false;
    }
    return patient.appointmentTests.some(test => 
      test.availableTest?.test_code === 'P11954' || test.availableTest?.test_code === 'P11953'
    );
  };

  // Get active profile goal
  const getActiveProfileGoal = () => {
    if (!patient.profile?.profileGoals || !Array.isArray(patient.profile.profileGoals)) {
      return null;
    }
    return patient.profile.profileGoals.find(goal => goal.is_active);
  };

  // Check if patient has blood test code (starts with P)
  const hasBloodTest = () => {
    if (!patient.appointmentTests || !Array.isArray(patient.appointmentTests)) {
      return false;
    }
    return patient.appointmentTests.some(test => 
      test.availableTest?.test_code && test.availableTest.test_code.startsWith('P')
    );
  };

  const getOverallStatus = () => {
    const dexaStatus = patient.dexaReport?.report_status;
    const vo2maxStatus = patient.vo2maxReport?.report_status;
    const hasBloodReport = !!bloodReport?.report_url;
    
    if ((dexaStatus === 'published' || vo2maxStatus === 'published') && hasBloodReport) {
      return { text: 'Completed', class: 'bg-orange-500 text-white rounded-lg' };
    }
    if (dexaStatus || vo2maxStatus || hasBloodReport) {
      return { text: 'In Progress', class: 'bg-orange-500 text-white rounded-lg' };
    }
    return { text: 'Not Started', class: 'bg-orange-500 text-white rounded-lg' };
  };
  
  // Get payment status
  const getPaymentStatus = () => {
    const paymentId = patient.payment_id;
    if (paymentId && paymentId.trim() !== '') {
      return { status: 'paid', icon: FaDollarSign, color: 'text-green-500', tooltip: 'Paid' };
    }
    return { status: 'pending', icon: FaClock, color: 'text-orange-500', tooltip: 'Pending' };
  };
  
  // Use patientStatus instead of status to avoid conflict with global status
  const patientStatus = getOverallStatus();
  const paymentStatus = getPaymentStatus();

  const getBloodButtonStyle = () => {
    if (bloodReport?.report_url) {
      return 'bg-green-100 hover:bg-green-200 text-green-800';
    }
    return 'bg-gray-100 hover:bg-gray-200 text-gray-600';
  };

  const getDexaButtonStyle = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 hover:bg-green-200 text-green-800';
      case 'submitted':
        return 'bg-yellow-50 hover:bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 hover:bg-gray-200 text-gray-600';
    }
  };

  const handleDexaButtonClick = () => {
    const isPublished = patient.dexaReport?.report_status === 'published';
    const hasDexaReport = !!dexaReport?.report_url;
    
    if (isPublished && hasDexaReport) {
      setDexaPDFModalOpen(true);
    } else {
      navigate(`/report/${patient.id}`);
    }
  };
  
  const handleVO2MaxButtonClick = () => {
    // Check if PDF URL exists from either source
    const hasPDF = !!vo2maxReport?.report_url || !!patient.vo2maxReport?.pdf_url;
    
    if (hasPDF) {
      // If PDF exists, open the modal
      setVO2MaxPDFModalOpen(true);
    } else {
      // Otherwise navigate to the report form
      navigate(`/vo2max-report/${patient.id}`);
    }
  };

  const handleSummaryReportButtonClick = () => {
    // Check if PDF URL exists from either source
    const hasPDF = !!summaryReport?.report_url || !!patient.summaryReport?.pdf_url;
    
    if (hasPDF) {
      // If PDF exists, open the modal
      setSummaryPDFModalOpen(true);
    } else {
      // Otherwise navigate to the report form
      navigate(`/summary-report/${patient.id}`);
    }
  };

  const handleStrengthReportButtonClick = () => {
    // Check if PDF URL exists from either source
    const hasPDF = !!strengthReport?.report_url || !!patient.strengthReport?.pdf_url;
    
    if (hasPDF) {
      // If PDF exists, open the modal
      setStrengthPDFModalOpen(true);
    } else {
      // Otherwise navigate to the report form
      navigate(`/strength-report/${patient.id}`);
    }
  };

  const handleAlzheimerReportButtonClick = () => {
    // Check if PDF URL exists from either source
    const hasPDF = !!alzheimerReport?.report_url || !!patient.alzheimerReport?.pdf_url;
    
    if (hasPDF) {
      // If PDF exists, open the modal
      setAlzheimerPDFModalOpen(true);
    } else {
      // Otherwise navigate to the report form
      navigate(`/alzheimer-report/${patient.id}`);
    }
  };

  const handleFatLossReportButtonClick = () => {
    const hasPDF = !!fatLossReport?.report_url || !!patient.fatLossReport?.pdf_url;
    
    if (hasPDF) {
      setFatLossPDFModalOpen(true);
    } else {
      navigate(`/fatloss-report/${patient.id}`);
    }
  };

  const getStrengthReportButtonStyle = (status) => {
    // If report has PDF URL, show green
    if (strengthReport?.report_url || patient.strengthReport?.pdf_url) {
      return 'bg-green-100 hover:bg-green-200 text-green-800';
    }
    
    // If report exists but no PDF (submitted state), show yellow
    if (status === 'submitted' || patient.strengthReport) {
      return 'bg-yellow-50 hover:bg-yellow-100 text-yellow-800';
    }
    
    // Default gray for no report
    return 'bg-gray-100 hover:bg-gray-200 text-gray-600';
  };

  const getSummaryReportButtonStyle = (status) => {
    // If report has PDF URL, show green
    if (summaryReport?.report_url || patient.summaryReport?.pdf_url) {
      return 'bg-green-100 hover:bg-green-200 text-green-800';
    }
    
    // If report exists but no PDF (submitted state), show yellow
    if (status === 'submitted' || patient.summaryReport) {
      return 'bg-yellow-50 hover:bg-yellow-100 text-yellow-800';
    }
    
    // Default gray for no report
    return 'bg-gray-100 hover:bg-gray-200 text-gray-600';
  };

  return (
    <Card className="border-l-[3px] border-orange-500 p-3 hover:shadow-sm transition-all duration-200">
      <div className="mb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm">{patient.profile?.name || 'Unknown'}</h3>
            <div className="flex items-center gap-1">
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium border border-blue-100">
                {patient.profile?.age || '-'}/{patient.profile?.gender?.charAt(0)?.toUpperCase() || '-'}
              </span>
              {getActiveProfileGoal() && (
                <FaBullseye className="text-green-500" size={12} />
              )}
              <div title={paymentStatus.tooltip}>
                {paymentStatus.status === 'paid' ? (
                  <FaDollarSign className={paymentStatus.color} size={12} />
                ) : (
                  <FaClock className={paymentStatus.color} size={12} />
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setDetailsModalOpen(true)}
            className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="View Details"
          >
            <FaInfo size={14} />
          </button>
        </div>
      </div>

      <div className="text-xs mb-3 grid grid-cols-2 gap-x-2">
        <div>
          <span className="font-small">Sukra ID:</span> 
          <span className="ml-1 text-gray-900 font-medium"> {patient.sukra_id || <span className="text-red-500">N/A</span>}</span>
        </div>
        <div>
          <span className="font-small">S ID:</span>
          <span className="ml-1 text-gray-900 font-medium">{patient.sukra_order_id || <span className="text-red-500">N/A</span>}</span>
        </div>
      </div>

      <div className="text-xs mb-3 grid grid-cols-2 gap-x-2">
        <div>
          <span className="font-small">Branch:</span> 
          <span className="ml-1 text-gray-900  font-medium">{patient.facility?.branch_name ? 
            patient.facility.branch_name.replace('VITAL INSIGHTS-', '').split('-')[0] : 'Unknown'}</span>
        </div>
        <div>
          <span className="font-small">Time:</span>
          <span className="ml-1 text-gray-900 font-medium">{patient.slot_start_time ? (() => {
            const isoString = patient.slot_start_time;
            const datePart = isoString.split('T')[0];
            const timePart = isoString.split('T')[1];
            const [year, month, day] = datePart.split('-');
            const [hours24, minutes] = timePart.split(':');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthName = monthNames[parseInt(month) - 1];
            const dayNum = parseInt(day);
            const hours = parseInt(hours24);
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const suffix = dayNum % 10 === 1 && dayNum !== 11 ? 'st' : dayNum % 10 === 2 && dayNum !== 12 ? 'nd' : dayNum % 10 === 3 && dayNum !== 13 ? 'rd' : 'th';
            return `${dayNum}${suffix} ${monthName}, ${displayHours}:${minutes} ${ampm}`;
          })() : <span className="text-red-500">N/A</span>}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Blood Report */}
        {hasBloodTest() && (
          <div className="relative flex-1 min-w-[140px]" ref={dropdownRef}>
            <button
              onClick={() => {
                if (bloodReport?.report_url) {
                  setBloodPDFModalOpen(true);
                } else {
                  setShowBloodConfirm(true);
                }
              }}
              disabled={isNotifying || !patient.sukra_order_id}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                bloodReport?.report_url 
                  ? 'bg-green-100 hover:bg-green-200 text-green-700' 
                  : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="flex items-center gap-2">
                {isNotifying ? (
                  <span className="animate-spin"><FaSync size={14} /></span>
                ) : bloodReport?.report_url ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>Blood</span>
              </span>
              {bloodReport?.report_url && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'blood' ? null : 'blood');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'blood' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'blood' && bloodReport?.report_url && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowBloodConfirm(true);
                    setOpenDropdown(null);
                  }}
                  disabled={isNotifying || !patient.sukra_order_id}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  <FaSync size={12} className="text-blue-500" />
                  <span>Regenerate</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* DEXA Report */}
        {hasDexaTest() && (
          <div className="relative flex-1 min-w-[140px]">
            <button
              onClick={() => {
                if (patient.dexaReport?.report_status === 'published') {
                  setDexaPDFModalOpen(true);
                } else {
                  handleDexaButtonClick();
                }
              }}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                patient.dexaReport?.report_status === 'published'
                  ? 'bg-green-100 hover:bg-green-200 text-green-700'
                  : 'bg-purple-100 hover:bg-purple-200 text-purple-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {patient.dexaReport?.report_status === 'published' ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>DEXA</span>
              </span>
              {patient.dexaReport?.report_status === 'published' && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'dexa' ? null : 'dexa');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'dexa' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'dexa' && patient.dexaReport?.report_status === 'published' && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => {
                    navigate(`/report/${patient.id}`);
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-purple-50 text-gray-700 flex items-center gap-2 transition-colors"
                >
                  <FaEdit size={12} className="text-purple-500" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* VO2Max Report */}
        {hasVO2MaxTest() && (
          <div className="relative flex-1 min-w-[140px]">
            <button
              onClick={() => {
                if (vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url) {
                  setVO2MaxPDFModalOpen(true);
                } else {
                  handleVO2MaxButtonClick();
                }
              }}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url
                  ? 'bg-green-100 hover:bg-green-200 text-green-700'
                  : 'bg-red-100 hover:bg-red-200 text-red-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>VO2 Max</span>
              </span>
              {(vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url) && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'vo2max' ? null : 'vo2max');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'vo2max' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'vo2max' && (vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => {
                    navigate(`/vo2max-report/${patient.id}`);
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-gray-700 flex items-center gap-2 transition-colors"
                >
                  <FaEdit size={12} className="text-red-500" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Summary Report */}
        {hasSummaryReportTest() && (
          <div className="relative flex-1 min-w-[140px]" ref={dropdownRef}>
            <button
              onClick={() => {
                if (summaryReport?.report_url || patient.summaryReport?.pdf_url) {
                  setSummaryPDFModalOpen(true);
                } else {
                  handleSummaryReportButtonClick();
                }
              }}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                summaryReport?.report_url || patient.summaryReport?.pdf_url
                  ? 'bg-green-100 hover:bg-green-200 text-green-700'
                  : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {summaryReport?.report_url || patient.summaryReport?.pdf_url ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>Summary</span>
              </span>
              {(summaryReport?.report_url || patient.summaryReport?.pdf_url) && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  console.log('Summary dropdown clicked, current state:', openDropdown);
                  setOpenDropdown(openDropdown === 'summary' ? null : 'summary');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'summary' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'summary' && (summaryReport?.report_url || patient.summaryReport?.pdf_url) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => {
                    console.log('Edit Summary Report clicked - patient.id:', patient.id);
                    navigate(`/summary-report/${patient.id}`);
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 text-gray-700 flex items-center gap-2 transition-colors"
                >
                  <FaEdit size={12} className="text-indigo-500" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Strength Report */}
        {hasStrengthReportTest() && (
          <div className="relative flex-1 min-w-[140px]">
            <button
              onClick={() => {
                if (strengthReport?.report_url || patient.strengthReport?.pdf_url) {
                  setStrengthPDFModalOpen(true);
                } else {
                  handleStrengthReportButtonClick();
                }
              }}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                strengthReport?.report_url || patient.strengthReport?.pdf_url
                  ? 'bg-green-100 hover:bg-green-200 text-green-700'
                  : 'bg-teal-100 hover:bg-teal-200 text-teal-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {strengthReport?.report_url || patient.strengthReport?.pdf_url ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>Strength</span>
              </span>
              {(strengthReport?.report_url || patient.strengthReport?.pdf_url) && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'strength' ? null : 'strength');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'strength' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'strength' && (strengthReport?.report_url || patient.strengthReport?.pdf_url) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => {
                    navigate(`/strength-report/${patient.id}`);
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-teal-50 text-gray-700 flex items-center gap-2 transition-colors"
                >
                  <FaEdit size={12} className="text-teal-500" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Alzheimer Report */}
        {hasAlzheimerReportTest() && (
          <div className="relative flex-1 min-w-[140px]">
            <button
              onClick={() => {
                if (alzheimerReport?.report_url || patient.alzheimerReport?.pdf_url) {
                  setAlzheimerPDFModalOpen(true);
                } else {
                  handleAlzheimerReportButtonClick();
                }
              }}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                alzheimerReport?.report_url || patient.alzheimerReport?.pdf_url
                  ? 'bg-green-100 hover:bg-green-200 text-green-700'
                  : 'bg-pink-100 hover:bg-pink-200 text-pink-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {alzheimerReport?.report_url || patient.alzheimerReport?.pdf_url ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>Alzheimer</span>
              </span>
              {(alzheimerReport?.report_url || patient.alzheimerReport?.pdf_url) && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'alzheimer' ? null : 'alzheimer');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'alzheimer' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'alzheimer' && (alzheimerReport?.report_url || patient.alzheimerReport?.pdf_url) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => {
                    navigate(`/alzheimer-report/${patient.id}`);
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-pink-50 text-gray-700 flex items-center gap-2 transition-colors"
                >
                  <FaEdit size={12} className="text-pink-500" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Fat Loss Report */}
        {hasFatLossReportTest() && (
          <div className="relative flex-1 min-w-[140px]">
            <button
              onClick={() => {
                if (fatLossReport?.report_url || patient.fatLossReport?.pdf_url) {
                  setFatLossPDFModalOpen(true);
                } else {
                  handleFatLossReportButtonClick();
                }
              }}
              className={`w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 ${
                fatLossReport?.report_url || patient.fatLossReport?.pdf_url
                  ? 'bg-green-100 hover:bg-green-200 text-green-700'
                  : 'bg-orange-100 hover:bg-orange-200 text-orange-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {fatLossReport?.report_url || patient.fatLossReport?.pdf_url ? (
                  <FaEye size={14} />
                ) : (
                  <FaPlus size={14} />
                )}
                <span>Fat Loss</span>
              </span>
              {(fatLossReport?.report_url || patient.fatLossReport?.pdf_url) && (
                <div className="p-1 cursor-pointer hover:bg-white hover:bg-opacity-50 rounded transition-colors" onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'fatloss' ? null : 'fatloss');
                }}>
                  <FaChevronDown size={10} className={`transition-transform ${openDropdown === 'fatloss' ? 'rotate-180' : ''}`} />
                </div>
              )}
            </button>
            
            {openDropdown === 'fatloss' && (fatLossReport?.report_url || patient.fatLossReport?.pdf_url) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => {
                    navigate(`/fatloss-report/${patient.id}`);
                    setOpenDropdown(null);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 text-gray-700 flex items-center gap-2 transition-colors"
                >
                  <FaEdit size={12} className="text-orange-500" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Lab Results */}
        {hasBloodTest() && (
          <div className="relative flex-1 min-w-[140px]">
            <button
              onClick={() => navigate(`/lab-results/${patient.id}`)}
              className="w-full py-2 px-3 rounded text-xs font-semibold flex items-center justify-between gap-2 transition-all duration-200 bg-cyan-100 hover:bg-cyan-200 text-cyan-700"
            >
              <span className="flex items-center gap-2">
                <FaEdit size={14} />
                <span>Lab Results</span>
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Blood Report Modal */}
      <PDFModal
        isOpen={isBloodPDFModalOpen}
        onClose={() => setBloodPDFModalOpen(false)}
        pdfUrl={bloodReport?.report_url}
      />

      {/* Blood Test Warning Modal */}
      <ValidationModal
        isOpen={showWarningModal}
        warningMessage={warningMessage}
        onClose={() => {
          setShowWarningModal(false);
          setPendingBloodTestRequest(null);
        }}
        onForceCreate={handleForceBloodTestCreate}
      />

      {/* DEXA Report Modal */}
      <PDFModal
        isOpen={isDexaPDFModalOpen}
        onClose={() => setDexaPDFModalOpen(false)}
        pdfUrl={dexaReport?.report_url}
      />
      
      {/* VO2Max Report Modal */}
      <PDFModal
        isOpen={isVO2MaxPDFModalOpen}
        onClose={() => setVO2MaxPDFModalOpen(false)}
        pdfUrl={vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url}
      />
      
      {/* Summary Report Modal */}
      <PDFModal
        isOpen={isSummaryPDFModalOpen}
        onClose={() => setSummaryPDFModalOpen(false)}
        pdfUrl={summaryReport?.report_url || patient.summaryReport?.pdf_url}
      />
      
      {/* Strength Report Modal */}
      <PDFModal
        isOpen={isStrengthPDFModalOpen}
        onClose={() => setStrengthPDFModalOpen(false)}
        pdfUrl={strengthReport?.report_url || patient.strengthReport?.pdf_url}
      />
      
      {/* Alzheimer Report Modal */}
      <PDFModal
        isOpen={isAlzheimerPDFModalOpen}
        onClose={() => setAlzheimerPDFModalOpen(false)}
        pdfUrl={alzheimerReport?.report_url || patient.alzheimerReport?.pdf_url}
      />
      
      {/* Fat Loss Report Modal */}
      <PDFModal
        isOpen={isFatLossPDFModalOpen}
        onClose={() => setFatLossPDFModalOpen(false)}
        pdfUrl={fatLossReport?.report_url || patient.fatLossReport?.pdf_url}
      />
      
      {/* Details Modal */}
      <DetailsModal
        data={patient}
        isOpen={isDetailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      />
      
      {/* Custom Notification */}
      {showNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-sm mx-4 border-l-4 border-orange-500">
            <div className="flex items-start gap-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-gray-800 font-medium">{notificationMessage}</p>
              </div>
              <button 
                onClick={() => setShowNotification(false)} 
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blood Report Category Selection Modal */}
      {showBloodConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Generate Blood Report</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will request a blood report from the server. The process may take approximately 1 to 2 minutes.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-blue-900 mb-3">
                Select Health Categories
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-blue-200 rounded-lg p-3 bg-blue-50">
                {HEALTH_CATEGORIES.map((category) => (
                  <label key={category.id} className="flex items-center gap-3 cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => handleCategoryToggle(category.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer accent-blue-600"
                    />
                    <span className="text-sm text-gray-700">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-6 space-y-2 border border-blue-200 rounded-lg p-3 bg-blue-50">
              <label className="flex items-center gap-3 cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={includeAbnormalBiomarkers}
                  onChange={(e) => setIncludeAbnormalBiomarkers(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer accent-blue-600"
                />
                <span className="text-sm text-gray-700">Biomarkers of Concern</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer hover:bg-blue-100 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={includeResultsAtGlance}
                  onChange={(e) => setIncludeResultsAtGlance(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer accent-blue-600"
                />
                <span className="text-sm text-gray-700">Results at a Glance</span>
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleBloodConfirmClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifyBloodTest}
                disabled={isNotifying}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
              >
                {isNotifying ? (
                  <>
                    <FaSync size={14} className="animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <span>Generate Report</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PatientCard;
